import { DropdownMenu } from "@kobalte/core";
import { useNavigate } from "@solidjs/router";
import { clearAllTokens } from "../lib/auth";
import { useTheme } from "../lib/useTheme";
import { useColor } from "../lib/useColor";
import { useAuth } from "../context/auth";
import { twMerge } from "tailwind-merge";
import Check from "@icons/check";
import CircleUserRound from "@icons/circle-user-round";
import LogOut from "@icons/log-out";
import Moon from "@icons/moon";
import Sun from "@icons/sun";

const colorMap: Record<string, string> = {
  "color-green": "bg-green-500",
  "color-yellow": "bg-yellow-400",
  "color-violet": "bg-violet-500",
  "color-blue": "bg-blue-500",
};

export default function Profile() {
  const navigate = useNavigate();
  const [auth, { clearUser }] = useAuth();
  const [theme, setTheme] = useTheme();
  const [color, setColor] = useColor();

  const name = auth.user?.name ?? "U";

  const logout = () => {
    clearAllTokens();
    clearUser();
    navigate("/login");
  };

  const handleSwitchTheme = () => {
    setTheme(theme() === "light" ? "dark" : "light");
  };

  const handleChangeColor = (newColor: string) => {
    setColor(newColor);
  };

  return (
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger
        class={twMerge(
          "inline-flex items-center justify-center rounded-md bg-[var(--color-primary)] cursor-pointer text-sm font-medium text-white hover:bg-[var(--color-secondary)] focus:outline-none size-8",
          ["color-yellow"].includes(color()) && "text-gray-800"
        )}
      >
        {name.charAt(0)}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content class="absolute z-[100] right-0 p-1 mt-2 w-56 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-[var(--bg-secondary)]">
          {/* Profile link */}
          <DropdownMenu.Item
            onSelect={() => navigate("/profile")}
            class={
              "flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer text-sm text-gray-900 hover:bg-[var(--color-primary-opacity)] dark:hover:bg-transparent hover:text-[var(--color-primary)] dark:text-white"
            }
          >
            <CircleUserRound class="size-5" />
            <span class="line-clamp-1">{name}</span>
          </DropdownMenu.Item>
          <DropdownMenu.Item class="flex items-center justify-center gap-3 p-2">
            {["color-green", "color-yellow", "color-violet", "color-blue"].map(
              (c) => (
                <DropdownMenu.Item
                  onSelect={() => handleChangeColor(c)}
                  class={twMerge(
                    `flex size-5 items-center justify-center rounded-full ${colorMap[c]} cursor-pointer`,
                    color() === c && "text-white"
                  )}
                >
                  {color() === c && (
                    <Check
                      size={14}
                      class={twMerge(
                        "font-semibold",
                        c === "color-yellow" ? "text-gray-800" : "text-white"
                      )}
                    />
                  )}
                </DropdownMenu.Item>
              )
            )}
          </DropdownMenu.Item>

          {/* Theme toggle */}
          <DropdownMenu.Item
            onSelect={handleSwitchTheme}
            class={
              "flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer text-sm text-gray-900 hover:bg-[var(--color-primary-opacity)] dark:hover:bg-transparent hover:text-[var(--color-primary)] dark:text-white"
            }
          >
            {theme() === "light" ? (
              <Moon class="size-5" />
            ) : (
              <Sun class="size-5" />
            )}
            Tema {theme() === "light" ? "Hitam" : "Putih"}
          </DropdownMenu.Item>

          <DropdownMenu.Item
            onSelect={logout}
            class={
              "flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer text-sm text-gray-900 hover:bg-[var(--color-primary-opacity)] dark:hover:bg-transparent hover:text-[var(--color-primary)] dark:text-white"
            }
          >
            <LogOut class="size-5" />
            Keluar
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
